import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PTZPanel from '../components/onvif/PTZPanel';
import ImagingPanel from '../components/onvif/ImagingPanel';
import EncodingPanel from '../components/onvif/EncodingPanel';
import NetworkPanel from '../components/onvif/NetworkPanel';
import OverlayPanel from '../components/onvif/OverlayPanel';
import * as onvifApi from '../services/onvif.service';
import { getNodeCameraNames } from '../services/api.service';
import StreamPreview from '../components/onvif/StreamPreview';
import { PageTitle } from '@trinetra/shared';
import {
  ArrowLeftIcon,
  VideoCameraIcon,
  ArrowsPointingOutIcon,
  PhotoIcon,
  FilmIcon,
  GlobeAltIcon,
  ViewfinderCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

type Tab = 'ptz' | 'imaging' | 'encoding' | 'network' | 'overlay';

const tabItems: { key: Tab; label: string; short: string; icon: React.FC<any> }[] = [
  { key: 'ptz', label: 'PTZ Control', short: 'PTZ', icon: ArrowsPointingOutIcon },
  { key: 'imaging', label: 'Image', short: 'Image', icon: PhotoIcon },
  { key: 'encoding', label: 'Encoding', short: 'Encoding', icon: FilmIcon },
  { key: 'network', label: 'Network', short: 'Network', icon: GlobeAltIcon },
  { key: 'overlay', label: 'Overlay', short: 'Overlay', icon: ViewfinderCircleIcon },
];

/**
 * Peel the {success, data} envelopes the local backend and the cloud tunnel each add, so a
 * payload reads the same however many hops it took to get here.
 */
function unwrap(resp: any): any {
  let node = resp;
  for (let i = 0; i < 4; i++) {
    if (node && typeof node === 'object' && 'data' in node && ('success' in node || 'message' in node)) {
      node = node.data;
    } else break;
  }
  return node;
}

/** Small labelled fact in the page header — model, firmware, address. */
function DeviceChip({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1 text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-foreground">{value}</span>
    </span>
  );
}

function PanelSkeleton() {
  return (
    <div className="animate-pulse space-y-4 rounded-xl border border-border bg-card p-5 shadow-card">
      <div className="h-4 w-32 rounded bg-muted" />
      <div className="h-3 w-56 rounded bg-muted" />
      <div className="space-y-3 pt-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-24 rounded bg-muted" />
            <div className="h-8 w-full rounded-lg bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CameraConfigPage() {
  const { nodeId, cameraId } = useParams<{ nodeId: string; cameraId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('imaging');
  const [config, setConfig] = useState<any>(null);
  const [capabilities, setCapabilities] = useState<any>(null);
  // The name operators actually know the camera by. It lives in the node's own record, not in
  // anything ONVIF reports, so the page could previously only identify a camera by vendor model
  // or a truncated UUID — neither of which tells you which doorway you are looking at.
  const [cameraName, setCameraName] = useState<string | null>(null);
  const [ptzStatus, setPtzStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Set while the panel on the active tab holds edits that have not been applied yet.
  const [dirty, setDirty] = useState(false);
  // The tab the user tried to reach while edits were pending, held until they decide.
  const [pendingTab, setPendingTab] = useState<Tab | null>(null);
  // A PTZ camera should open on PTZ — but never yank the user off a tab they chose themselves.
  const tabChosen = useRef(false);
  const successTimer = useRef<any>(null);
  // Clears a transient PTZ warning once the control is usable again.
  const ptzNoticeTimer = useRef<any>(null);

  const load = useCallback(async () => {
    if (!nodeId || !cameraId) return;
    setLoading(true);
    setError(null);
    try {
      // Only a label: never let it fail the page the way a missing config would. The names
      // endpoint is also the cheapest source (id + name) and the least privileged.
      const namesReq = getNodeCameraNames(nodeId).catch(() => null);
      // Config and capabilities one after the other, never together: the agent serves one
      // request per camera at a time and turns away one that waits more than a few seconds, so
      // firing both at once made capabilities (and with it every encoding option) fail with
      // "Camera is busy" whenever the config read was slow — and the whole page with it.
      const cfgRes = await onvifApi.getConfig(nodeId, cameraId);
      const capRes = await onvifApi.getCapabilities(nodeId, cameraId);
      const names = await namesReq;
      const cfg = unwrap(cfgRes);
      const caps = unwrap(capRes);
      setConfig(cfg);
      setCapabilities(caps);
      // Through the tunnel this arrives either bare or still wrapped, as the other node adapters
      // in this app also have to allow for.
      const nameList: any[] = Array.isArray(names) ? names : names?.data || [];
      setCameraName(nameList.find((c: any) => c.id === cameraId)?.name || null);
      setVersion(cfg?.version || 0);
      setPtzStatus(caps?.ptz?.status || null);
      if (!tabChosen.current && caps?.ptz_supported) setTab('ptz');
    } catch (err: any) {
      setError(err.message || 'Failed to load camera config');
    } finally {
      setLoading(false);
    }
  }, [nodeId, cameraId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => {
    clearTimeout(successTimer.current);
    clearTimeout(ptzNoticeTimer.current);
  }, []);


  // Closing the tab mid-edit loses the change silently otherwise.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = async (section: string, data: any) => {
    if (!nodeId || !cameraId) return;
    setLoading(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const fns: Record<string, Function> = {
        network: onvifApi.updateNetwork,
        imaging: onvifApi.updateImaging,
        encoding: onvifApi.updateEncoding,
        overlay: onvifApi.updateOverlay,
      };
      await fns[section](nodeId, cameraId, version, data);
      await load();
      setSaveSuccess(true);
      clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePTZ = useCallback(async (cmd: any) => {
    if (!nodeId || !cameraId) return null;
    try {
      return unwrap(await onvifApi.sendPTZ(nodeId, cameraId, cmd));
    } catch (err: any) {
      const status = err.response?.status;
      setError(err.response?.data?.error || err.response?.data?.detail
        || err.message || 'PTZ command failed');
      // "Camera is busy" (503) and "camera did not answer, it may still be moving" (504) are
      // momentary — the agent bounds both, and the next press usually lands. A warning that
      // stays on screen after the control has recovered reads as a broken panel, so these
      // clear themselves; a genuine fault still stays put until the user acts on it.
      if (status === 503 || status === 504) {
        clearTimeout(ptzNoticeTimer.current);
        ptzNoticeTimer.current = setTimeout(() => setError(null), 2500);
      }
      return null;
    }
  }, [nodeId, cameraId]);

  // PTZ status rides the existing ptz endpoint as `action: "status"`, so polling the live
  // position costs no extra route through the cloud tunnel.
  // Returns the reading as well as storing it: the panel needs to know whether the head has
  // finished moving to decide when it may send the next command.
  const refreshPtzStatus = useCallback(async () => {
    if (!nodeId || !cameraId || !capabilities?.ptz_supported) return null;
    try {
      const status = unwrap(await onvifApi.sendPTZ(nodeId, cameraId, { action: 'status' }));
      if (status && typeof status === 'object') {
        setPtzStatus(status);
        return status;
      }
    } catch {
      // A camera that won't report its position still drives fine; leave the last reading.
    }
    return null;
  }, [nodeId, cameraId, capabilities?.ptz_supported]);

  const handleAux = useCallback((auxData: string) => {
    handlePTZ({ action: 'aux', aux_data: auxData });
  }, [handlePTZ]);

  const openTab = (key: Tab) => {
    tabChosen.current = true;
    if (key === tab) return;
    if (dirty) { setPendingTab(key); return; }
    setTab(key);
  };

  const discardAndSwitch = () => {
    setDirty(false);
    if (pendingTab) setTab(pendingTab);
    setPendingTab(null);
  };

  /** Arrow keys walk the tab strip, which is what a tablist is expected to do. */
  const onTabKeyDown = (e: React.KeyboardEvent) => {
    const usable = tabItems.filter((t) => !(t.key === 'ptz' && capabilities && !capabilities.ptz_supported));
    const i = usable.findIndex((t) => t.key === tab);
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = usable[(i + (e.key === 'ArrowRight' ? 1 : usable.length - 1)) % usable.length];
      if (next) openTab(next.key);
    }
  };

  const device = capabilities?.device;
  const deviceName = device?.manufacturer || device?.model
    ? `${device.manufacturer || ''} ${device.model || ''}`.trim()
    : null;

  return (
    <div className="pb-4">
      {/* Title bar: back + which camera this is, then device facts and Refresh. */}
      <PageTitle
        title={(
          <>
            <button type="button" onClick={() => navigate(-1)} title="Back" aria-label="Back" className="mr-1.5 inline-flex rounded p-0.5 align-[-3px] text-muted-foreground hover:bg-muted hover:text-foreground">
              <ArrowLeftIcon className="h-4 w-4" />
            </button>
            <span title={cameraName || undefined}>
              {cameraName || deviceName || <span className="font-mono">{cameraId?.slice(0, 8)}…</span>}
            </span>
          </>
        )}
        subtitle={`Camera Configuration${cameraName && deviceName ? ` · ${deviceName}` : ''}`}
      >
        <div className="hidden items-center gap-1.5 lg:flex">
          <DeviceChip label="fw" value={device?.firmware} />
          <DeviceChip label="ip" value={config?.network?.ip} />
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </PageTitle>

      {/* Live Preview + Config side by side */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="space-y-3 lg:sticky lg:top-0">
            <StreamPreview nodeId={nodeId!} cameraId={cameraId!} />
            {/* Device facts belong next to the picture they describe, not squeezed into the header. */}
            {(device || config?.network || cameraName) && (
              <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                <p className="mb-3 text-[12px] font-semibold text-foreground">Device</p>
                <dl className="space-y-2.5">
                  {/* Third element opts a row out of the monospace treatment: it suits serials
                      and addresses, but a camera name is prose and reads badly in it. */}
                  {([
                    ['Name', cameraName, false],
                    ['Manufacturer', device?.manufacturer],
                    ['Model', device?.model],
                    ['Firmware', device?.firmware],
                    ['Serial', device?.serial_number || device?.serial],
                    ['Address', config?.network?.ip],
                    ['MAC', config?.network?.mac_address],
                  ] as [string, string | undefined | null, boolean?][]).filter(([, v]) => v).map(([label, value, mono = true]) => (
                    <div key={label} className="flex items-baseline justify-between gap-3">
                      <dt className="flex-shrink-0 text-[11px] text-muted-foreground">{label}</dt>
                      <dd className={`truncate text-[11px] text-foreground ${mono ? 'font-mono' : 'font-medium'}`}
                        title={String(value)}>
                        {value}
                      </dd>
                    </div>
                  ))}
                  <div className="flex items-baseline justify-between gap-3 border-t border-border pt-2.5">
                    <dt className="text-[11px] text-muted-foreground">PTZ</dt>
                    <dd className="text-[11px] font-medium text-foreground">
                      {capabilities?.ptz_supported ? 'Supported' : 'Not supported'}
                    </dd>
                  </div>
                </dl>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          {/* Tabs */}
          <div
            role="tablist" aria-label="Configuration sections" onKeyDown={onTabKeyDown}
            className="sticky top-0 z-10 mb-3 flex gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1.5 shadow-card backdrop-blur supports-[backdrop-filter]:bg-card"
          >
            {tabItems.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              const ptzDisabled = t.key === 'ptz' && capabilities && !capabilities.ptz_supported;
              return (
                <button
                  key={t.key}
                  role="tab" aria-selected={active} tabIndex={active ? 0 : -1}
                  onClick={() => !ptzDisabled && openTab(t.key)}
                  disabled={ptzDisabled}
                  title={ptzDisabled ? 'This camera does not support PTZ' : t.label}
                  className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-2.5 text-[13px] font-medium transition-all ${ptzDisabled
                      ? 'cursor-not-allowed text-muted-foreground'
                      : active
                        ? 'bg-brand text-white shadow-sm'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{t.short}</span>
                  {/* Unsaved work on the tab you are standing on, so it is never a surprise. */}
                  {active && dirty && (
                    <span className="h-1.5 w-1.5 rounded-full bg-white" title="Unsaved changes" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Panels */}
          <div role="tabpanel" aria-label={tabItems.find((t) => t.key === tab)?.label}>
          {loading && !config ? <PanelSkeleton /> : (
            <>
              {tab === 'ptz' && (capabilities?.ptz_supported === false
                ? <div className="rounded-xl border border-dashed border-border bg-card px-6 py-10 text-center text-[13px] text-muted-foreground">This camera does not support PTZ control.</div>
                : <PTZPanel onPTZ={handlePTZ} presets={capabilities?.ptz?.presets} loading={loading}
                    ptzCapabilities={capabilities?.ptz} status={ptzStatus} onRefreshStatus={refreshPtzStatus} />
              )}
              {tab === 'imaging' && <ImagingPanel config={config?.imaging} capabilities={capabilities?.imaging}
                ptzIlluminator={capabilities?.ptz?.illuminator} onAux={handleAux}
                onSave={(d: any) => save('imaging', d)} loading={loading} onDirtyChange={setDirty} />}
              {tab === 'encoding' && <EncodingPanel config={config?.encoding} capabilities={capabilities?.encoding}
                onSave={(d: any) => save('encoding', d)} loading={loading} onDirtyChange={setDirty} />}
              {tab === 'network' && <NetworkPanel nodeId={nodeId} cameraId={cameraId} config={config?.network}
                onSave={(d: any) => save('network', d)} loading={loading} onDirtyChange={setDirty} />}
              {tab === 'overlay' && <OverlayPanel config={config?.overlay}
                onSave={(d: any) => save('overlay', d)} loading={loading} onDirtyChange={setDirty} />}
            </>
          )}
          </div>
        </div>
      </div>

      {/* Feedback floats: applying a change from the bottom of a long panel used to report its
          result off-screen at the top of the page. */}
      <div className="pointer-events-none fixed bottom-4 right-4 z-40 flex w-[min(26rem,calc(100vw-2rem))] flex-col gap-2">
        {error && (
          <div className="pointer-events-auto flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 shadow-lg dark:border-red-800 dark:bg-red-950">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
            <p className="flex-1 text-[13px] font-medium text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError(null)} aria-label="Dismiss"
              className="text-red-400 transition-colors hover:text-red-600 dark:hover:text-red-200">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
        {saveSuccess && (
          <div className="pointer-events-auto flex items-center gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-lg dark:border-emerald-800 dark:bg-emerald-950">
            <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-emerald-500" />
            <p className="flex-1 text-[13px] font-medium text-emerald-700 dark:text-emerald-300">
              Settings applied to the camera
            </p>
          </div>
        )}
      </div>

      {/* Leaving a tab mid-edit */}
      {pendingTab && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-5 shadow-xl">
            <h2 className="text-[14px] font-semibold text-foreground">Discard unsaved changes?</h2>
            <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
              Changes on this tab have not been applied to the camera yet. Leaving now discards them.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setPendingTab(null)}
                className="rounded-lg border border-border px-3 py-2 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                Stay here
              </button>
              <button onClick={discardAndSwitch}
                className="rounded-lg bg-destructive px-3 py-2 text-[12px] font-medium text-destructive-foreground transition-colors hover:opacity-90">
                Discard &amp; switch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
